import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png";

export default function HalfSeasonSignup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    parent_first_name: "", parent_last_name: "", phone: "",
    address: "", city: "", state: "NY", zip_code: "",
    swim_level: "",
  });
  const [signatureType, setSignatureType] = useState("typed");
  const [typedSignature, setTypedSignature] = useState("");
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    axios.get(`${API}/api/half-season/invite/${token}`)
      .then(r => setInvite(r.data))
      .catch(e => setError(e.response?.data?.detail || "Invite not found"))
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas signature helpers
  const startDraw = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };
  const endDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    let signature = "";
    if (signatureType === "typed") {
      if (!typedSignature.trim()) { toast.error("Please type your full name to sign."); return; }
      signature = typedSignature.trim();
    } else {
      const c = canvasRef.current;
      // Detect blank canvas
      const ctx = c.getContext("2d");
      const data = ctx.getImageData(0, 0, c.width, c.height).data;
      const hasInk = Array.from(data).some((v, i) => i % 4 === 3 && v > 0);
      if (!hasInk) { toast.error("Please draw your signature."); return; }
      signature = c.toDataURL("image/png");
    }
    for (const k of ["parent_first_name","parent_last_name","phone","address","city","state","zip_code","swim_level"]) {
      if (!form[k]) { toast.error("Please fill in all fields."); return; }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/half-season/invite/${token}/complete`, {
        ...form, signature, signature_type: signatureType,
      });
      toast.success("Contract signed! Check your email for a copy.");
      navigate(`/confirmation/${res.data.registration_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center bg-red-50 border border-red-200 rounded-2xl p-8">
        <p className="text-red-700 font-bold mb-2">Invite Unavailable</p>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-2xl mx-auto px-4 flex items-center gap-3">
          <img src={LOGO} alt="Rolling River" className="h-12" />
          <div>
            <h1 className="font-bold text-[#2B4C8C]">Rolling River Day Camp</h1>
            <p className="text-xs text-slate-500">Half-Season Swim Lesson Signup</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 md:p-6 space-y-5">
        {/* Locked lesson summary */}
        <div className="bg-[#E6F5FD] border-2 border-[#109FE9] rounded-2xl p-5" data-testid="hs-invite-summary">
          <h2 className="font-bold text-[#0E8ACB] mb-3 text-lg">Lesson Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><span className="text-slate-500">Child:</span> <span className="font-semibold">{invite.child_first_name} {invite.child_last_name}</span></div>
            <div><span className="text-slate-500">Instructor:</span> <span className="font-semibold">{invite.lifeguard_name}</span></div>
            <div><span className="text-slate-500">Time:</span> <span className="font-semibold">{invite.time}</span></div>
            <div><span className="text-slate-500">Total Price:</span> <span className="font-bold text-[#2B4C8C]">${invite.total_price?.toFixed(2)}</span> <span className="text-xs text-slate-500">(${invite.price_per_lesson}/lesson × {invite.dates?.length})</span></div>
          </div>
          <div className="mt-3">
            <span className="text-slate-500 text-sm">Lesson Dates:</span>
            <ul className="text-sm font-semibold text-slate-700 mt-1 grid grid-cols-2 gap-1">
              {(invite.dates || []).map(d => <li key={d}>• {d}</li>)}
            </ul>
          </div>
        </div>

        {/* Parent form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 border border-slate-200 space-y-4">
          <h2 className="font-bold text-[#2B4C8C]">Parent Information</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First Name *</Label><Input data-testid="hs-parent-first" value={form.parent_first_name} onChange={e=>setForm({...form, parent_first_name:e.target.value})} required/></div>
            <div><Label>Last Name *</Label><Input data-testid="hs-parent-last" value={form.parent_last_name} onChange={e=>setForm({...form, parent_last_name:e.target.value})} required/></div>
            <div className="col-span-2"><Label>Email</Label><Input value={invite.parent_email} disabled className="bg-slate-100"/></div>
            <div className="col-span-2"><Label>Phone *</Label><Input data-testid="hs-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} required/></div>
            <div className="col-span-2"><Label>Address *</Label><Input data-testid="hs-address" value={form.address} onChange={e=>setForm({...form, address:e.target.value})} required/></div>
            <div><Label>City *</Label><Input data-testid="hs-city" value={form.city} onChange={e=>setForm({...form, city:e.target.value})} required/></div>
            <div><Label>State *</Label><Input data-testid="hs-state" value={form.state} onChange={e=>setForm({...form, state:e.target.value})} required/></div>
            <div className="col-span-2"><Label>ZIP Code *</Label><Input data-testid="hs-zip" value={form.zip_code} onChange={e=>setForm({...form, zip_code:e.target.value})} required/></div>
          </div>

          <div>
            <Label>Swim Level *</Label>
            <Select value={form.swim_level} onValueChange={v=>setForm({...form, swim_level:v})}>
              <SelectTrigger data-testid="hs-swim-level"><SelectValue placeholder="Select swim level"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Signature */}
          <div className="border-t pt-4 space-y-3">
            <h2 className="font-bold text-[#2B4C8C]">Sign Contract</h2>
            <p className="text-xs text-slate-500">By signing below, you agree to the Rolling River Day Camp swim lesson terms and authorize the camp to schedule the lessons above. Total of ${invite.total_price?.toFixed(2)} is due in cash within 7 days of signing.</p>
            <div className="flex gap-2">
              <button type="button" onClick={()=>setSignatureType("typed")} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${signatureType==="typed"?"bg-[#2B4C8C] text-white":"bg-slate-100 text-slate-600"}`} data-testid="hs-sig-typed-btn">Type Signature</button>
              <button type="button" onClick={()=>setSignatureType("canvas")} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${signatureType==="canvas"?"bg-[#2B4C8C] text-white":"bg-slate-100 text-slate-600"}`} data-testid="hs-sig-draw-btn">Draw Signature</button>
            </div>
            {signatureType === "typed" ? (
              <Input data-testid="hs-typed-signature" placeholder="Type your full name" value={typedSignature} onChange={e=>setTypedSignature(e.target.value)} className="font-handwriting text-xl"/>
            ) : (
              <div>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  data-testid="hs-canvas-signature"
                  className="w-full border-2 border-slate-300 rounded-lg bg-white cursor-crosshair touch-none"
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                />
                <button type="button" onClick={clearCanvas} className="text-xs text-slate-500 hover:underline mt-1">Clear</button>
              </div>
            )}
          </div>

          <Button data-testid="hs-submit" type="submit" disabled={submitting} className="w-full bg-[#2B4C8C] hover:bg-[#1e3666] text-white font-bold py-3 text-lg">
            {submitting ? "Signing…" : "Sign & Submit"}
          </Button>
        </form>
      </main>
    </div>
  );
}
